import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { CheckoutHeader } from '@liveauctioneers/hammer-ui-liveauctioneers/headerVariants/checkout';
import { CheckoutPage } from '@liveauctioneers/hammer-ui-payments/checkout';
import { useHammerCheckoutData } from '@liveauctioneers/hammer-ui-payments/hooks';
import { Address, MarketplaceProps, ShippingMethod } from '@liveauctioneers/hammer-ui-payments/types';
import { useEffect, useMemo, useRef } from 'react';

import { useNotificationDataProps } from '@/components/Header/hooks/useNotificationDataProps';
import { useGetViewMoreItemsInfo } from '@/pages/CheckoutATG/hooks/useGetViewMoreItemsInfo';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderDataIfNeeded } from '@/redux/modules/account/user/user.actions';
import {
    getBidderId,
    getUserData,
    getUserFirstName,
    getUserLastName,
} from '@/redux/modules/account/user/user.selectors';
import { fetchAllBidderBiddingInfoIfNeeded } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.actions';
import { getMyBidItemsCount } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.selectors';
import { fetchConversationFoldersIfNeeded } from '@/redux/modules/conversation/folders/conversationFolders.actions';
import { getUnreadInboxCount } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import { fetchIncompleteOrdersCountIfNeeded } from '@/redux/modules/wonItems/wonItems.actions';
import { getIncompleteOrderCount } from '@/redux/modules/wonItems/wonItems.selectors';
import {
    HeaderNavigationEventType,
    useTrackHeaderNavigationClicked,
    useTrackPayNowClicked,
} from '@/utils/amplitude/analytics';
import { useTrackMessageAuctionHouseClicked } from '@/utils/amplitude/analytics/checkout/trackMessageAuctionHouseClicked.analytics';
import { useTrackReviewAuctionHousePolicyClicked } from '@/utils/amplitude/analytics/checkout/trackReviewAuctionHousePolicyClicked.analytics';
import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';

import { useGetMarketplaceProps } from './hooks/useGetMarketplaceProps';

const MAXIMUM_WON_ITEMS_DISPLAY_COUNT = 10;

type Props = {
    setMarketplaceProps: (marketplaceProps: MarketplaceProps) => void;
};

const CheckoutPageATGBody = ({ setMarketplaceProps }: Props) => {
    const { auctionId } = useHammerCheckoutData();
    const dispatch = useAppDispatch();
    const isInitialRender = useRef(true);

    // region checkout page analytics callbacks
    const trackCTAClickedAnalytics = useTrackCTAClickedAnalytics();
    const trackPayNowClicked = useTrackPayNowClicked();
    // endregion checkout page analytics callbacks

    const viewMoreItemsInfo = useGetViewMoreItemsInfo();

    const bidderId = useAppSelector(getBidderId);
    const unreadCount = useAppSelector(getUnreadInboxCount);
    const upcomingBidsCount = useAppSelector(getMyBidItemsCount);
    const wonItemsCount = useAppSelector(getIncompleteOrderCount);
    const notificationData = useNotificationDataProps();

    //region header tracking functions
    const trackHeaderNavigationClick = useTrackHeaderNavigationClicked();

    const trackAlertsIconClicked = () => {
        trackHeaderNavigationClick({
            eventType: HeaderNavigationEventType.Alerts,
        });
    };

    const trackMyAccountIconClicked = () => {
        trackHeaderNavigationClick({
            eventType: HeaderNavigationEventType.MyAccount,
        });
    };

    const trackMyItemsIconClicked = () => {
        trackHeaderNavigationClick({
            eventType: HeaderNavigationEventType.MyItems,
        });
    };

    const trackWonItemsIconClicked = () => {
        trackHeaderNavigationClick({
            eventType: HeaderNavigationEventType.WonItems,
        });
    };
    //endregion

    //region has a question callbacks
    const trackMessageAuctionHouseClicked = useTrackMessageAuctionHouseClicked();
    const trackReviewAuctionHousePolicyClicked = useTrackReviewAuctionHousePolicyClicked();
    //endregion

    const firstName = useAppSelector(getUserFirstName);
    const lastName = useAppSelector(getUserLastName);
    const { addresses } = useAppSelector(getUserData);

    useGetMarketplaceProps({
        setMarketplaceProps,
    });

    const formattedAddresses = useMemo(() => {
        return addresses.map((address) => {
            return {
                address1: address.address1,
                address2: address.address2,
                address3: address.address3,
                city: address.city,
                countryCode: address.countryCode,
                firstName: address.firstName,
                lastName: address.lastName,
                organizationName: address.organizationName,
                phoneNumber: address.phoneNumber,
                postalCode: address.postalCode,
                state: address.state,
            } as Address;
        });
    }, [addresses]);

    useEffect(() => {
        if (isInitialRender.current) {
            dispatch(fetchConversationFoldersIfNeeded({}));
            dispatch(fetchAllBidderBiddingInfoIfNeeded(false));
            dispatch(
                fetchIncompleteOrdersCountIfNeeded({
                    bidderId,
                    force: true,
                })
            );
        }
        isInitialRender.current = false;
    }, [bidderId, dispatch]);

    if (!auctionId) {
        return (
            <div className="mx-auto my-[200px] flex w-full justify-center">
                <Loading size={32} />
            </div>
        );
    }

    return (
        <CheckoutPage
            addressList={formattedAddresses}
            analyticsTracking={{
                amplitude: {
                    trackContinueToPaymentClicked: () =>
                        trackCTAClickedAnalytics(AnalyticsCTAEvent.CheckoutPage.SaveShippingMethod),
                    trackContinueToReviewClicked: () =>
                        trackCTAClickedAnalytics(AnalyticsCTAEvent.CheckoutPage.SavePaymentMethod),
                    trackContinueToShippingMethodClicked: () =>
                        trackCTAClickedAnalytics(AnalyticsCTAEvent.CheckoutPage.SaveShippingAddress),
                    trackPayNowClicked: (shippingMethod: ShippingMethod) => trackPayNowClicked({ shippingMethod }),
                },
            }}
            header={
                <CheckoutHeader
                    callbacks={{
                        onAlertsClicked: trackAlertsIconClicked,
                        onMyAccountClicked: trackMyAccountIconClicked,
                        onMyItemsClicked: trackMyItemsIconClicked,
                        onWonItemsClicked: trackWonItemsIconClicked,
                    }}
                    notificationData={notificationData}
                    unreadCount={unreadCount}
                    upcomingBidsCount={upcomingBidsCount}
                    wonItemsCount={wonItemsCount}
                    wonItemsMaximumCount={MAXIMUM_WON_ITEMS_DISPLAY_COUNT}
                />
            }
            name={{
                first: firstName,
                last: lastName,
            }}
            onAddressAddedCallback={() => {
                dispatch(fetchBidderDataIfNeeded({ force: true }));
            }}
            questionBannerOnClickCallbacks={{
                onClickMessageHouse: trackMessageAuctionHouseClicked,
                onClickReviewAuctionHousePolicy: trackReviewAuctionHousePolicyClicked,
            }}
            viewMoreItemsInfo={viewMoreItemsInfo}
        />
    );
};

export default CheckoutPageATGBody;
